import Immutable, { List, Map } from 'immutable';
import { ProjectGroup } from 'records/ProjectGroup';
import { Role } from 'records/Role';
import { User } from 'records/User';
import { UserQuery } from 'records/UserQuery';

interface SortHandlers {
  [key: string]: (
    users: Immutable.OrderedMap<string, User>,
    direction: string,
    roles: Immutable.Map<string, Role>,
    groups: Immutable.OrderedMap<string, List<ProjectGroup>>
  ) => Immutable.OrderedMap<string, User>;
}

const sortHandlers: SortHandlers = {
  email: (
    users: Immutable.OrderedMap<string, User>,
    direction
  ): Immutable.OrderedMap<string, User> => {
    const sortedUsers = users.sort((userA, userB): number => {
      const a =
        direction === 'ascending'
          ? userA.email.toLowerCase().trim()
          : userB.email.toLowerCase().trim();
      const b =
        direction === 'ascending'
          ? userB.email.toLowerCase().trim()
          : userA.email.toLowerCase().trim();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedUsers;
  },
  name: (
    users: Immutable.OrderedMap<string, User>,
    direction
  ): Immutable.OrderedMap<string, User> => {
    const sortedUsers = users.sort((userA, userB): number => {
      const nameA =
        userA.profile.firstname.toLowerCase().trim() +
        userA.profile.lastname.toLowerCase().trim();
      const nameB =
        userB.profile.firstname.toLowerCase().trim() +
        userB.profile.lastname.toLowerCase().trim();
      const a = direction === 'ascending' ? nameA : nameB;
      const b = direction === 'ascending' ? nameB : nameA;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedUsers;
  },
  company: (
    users: Immutable.OrderedMap<string, User>,
    direction
  ): Immutable.OrderedMap<string, User> => {
    const sortedUsers = users.sort((userA, userB): number => {
      const a =
        direction === 'ascending'
          ? userA.profile.company.toLowerCase().trim()
          : userB.profile.company.toLowerCase().trim();
      const b =
        direction === 'ascending'
          ? userB.profile.company.toLowerCase().trim()
          : userA.profile.company.toLowerCase().trim();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedUsers;
  },
  role: (
    users: Immutable.OrderedMap<string, User>,
    direction
  ): Immutable.OrderedMap<string, User> => {
    const sortedUsers = users.sort((userA, userB): number => {
      const a =
        direction === 'ascending'
          ? userA.profile.role.toLowerCase().trim()
          : userB.profile.role.toLowerCase().trim();
      const b =
        direction === 'ascending'
          ? userB.profile.role.toLowerCase().trim()
          : userA.profile.role.toLowerCase().trim();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedUsers;
  },
  perm: (
    users: Immutable.OrderedMap<string, User>,
    direction,
    roles
  ): Immutable.OrderedMap<string, User> => {
    const sortedUsers = users.sort((userA, userB): number => {
      const roleA = roles.getIn([userA.id, 'role']);
      const roleB = roles.getIn([userB.id, 'role']);
      const a = direction === 'ascending' ? roleA : roleB;
      const b = direction === 'ascending' ? roleB : roleA;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedUsers;
  },
  groups: (
    users: Immutable.OrderedMap<string, User>,
    direction,
    roles,
    groups: Map<string, List<ProjectGroup>>
  ): Immutable.OrderedMap<string, User> => {
    const sortedUsers = users.sort((userA, userB): number => {
      const userAGroups = groups?.get(userA.id) || List<ProjectGroup>();
      const userBGroups = groups?.get(userB.id) || List<ProjectGroup>();

      const firstDiffInGroupList = (
        userAGroups: List<ProjectGroup>,
        userBGroups: List<ProjectGroup>
      ): number => {
        let i = 0;
        const groupAArr = Array.from(userAGroups);
        const groupBArr = Array.from(userBGroups);
        if (groupAArr === groupBArr) return i;
        while (
          groupAArr.length > 0 &&
          groupBArr.length > 0 &&
          groupAArr.length >= i &&
          groupBArr.length >= i &&
          groupAArr[i] === groupBArr[i]
        ) {
          i++;
        }
        return i;
      };

      const sortByIndex = firstDiffInGroupList(userAGroups, userBGroups);

      const userAFirstGroup =
        Array.from(userAGroups)[sortByIndex] || new ProjectGroup();
      const userBFirstGroup =
        Array.from(userBGroups)[sortByIndex] || new ProjectGroup();

      const a =
        direction === 'ascending'
          ? userAFirstGroup.name.toLowerCase()
          : userBFirstGroup.name.toLowerCase();
      const b =
        direction === 'ascending'
          ? userBFirstGroup.name.toLowerCase()
          : userAFirstGroup.name.toLowerCase();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedUsers;
  },
};

function sortUsers(
  unsortedUsers: Immutable.OrderedMap<string, User>,
  userQuery: UserQuery,
  roles: Immutable.Map<string, Role>,
  groups: Immutable.OrderedMap<string, List<ProjectGroup>>
): Immutable.OrderedMap<string, User> {
  const { sortField, sortDirection } = userQuery;

  if (sortHandlers[sortField]) {
    const sortedUsers = sortHandlers[sortField](
      unsortedUsers,
      sortDirection,
      roles,
      groups
    );

    return sortedUsers;
  }

  return unsortedUsers;
}

export default function queryUsers(
  users: Immutable.OrderedMap<string, User>,
  userQuery: UserQuery,
  roles: Immutable.Map<string, Role>,
  groups: Immutable.OrderedMap<string, List<ProjectGroup>>
): Immutable.OrderedMap<string, User> {
  const sortedUsers = sortUsers(users, userQuery, roles, groups);
  return sortedUsers;
}
